/**
 * Song Card Component
 *
 * Matches original HTML structure exactly for CSS compatibility.
 */

import type { Song, VoteType } from '@/types';
import { vote } from '@/stores/songs';
import { canVote, selectedGroup } from '@/stores/groups';
import { play, currentTrackId, isPlaying } from '@/stores/player';
import { toast } from '@/stores/ui';

interface SongCardProps {
  song: Song;
}

export function SongCard({ song }: SongCardProps) {
  const isCurrentlyPlaying = currentTrackId.value === song.spotifyId && isPlaying.value;
  const isPaused = currentTrackId.value === song.spotifyId && !isPlaying.value;

  // Check if we should hide vote counts during voting
  const inVotingPeriod = selectedGroup.value?.periods?.currentPhase === 'voting';
  const shouldHideVoteCounts = inVotingPeriod && !song.isOwnSong;

  const handlePlay = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    play(song);
  };

  const handleVote = async (e: Event, voteType: VoteType) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canVote.value) {
      toast.warning('Voting is not open yet');
      return;
    }

    if (song.isOwnSong) {
      toast.warning("You can't vote on your own song");
      return;
    }

    const success = await vote(song.id, voteType);
    if (!success) {
      toast.error('Failed to submit vote');
    }
  };

  // Build class names
  const cardClasses = ['song-card'];
  if (isCurrentlyPlaying) cardClasses.push('playing');
  if (isPaused) cardClasses.push('paused');

  return (
    <div
      class={cardClasses.join(' ')}
      data-spotify-id={song.spotifyId}
      data-song-id={song.id}
    >
      {/* Mobile floating badge */}
      <div class={`likes-pill mobile-floating-only ${shouldHideVoteCounts ? 'hidden-during-voting' : ''}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
        </svg>
        <span class="likes-count">{song.votes?.score || 0}</span>
      </div>

      {/* Album art */}
      <div class="song-image">
        <img
          src={song.albumArt || '/images/logo-transparent_buffer.png'}
          alt={`${song.title} by ${song.artist}`}
          loading="eager"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/logo-transparent_buffer.png';
          }}
        />
      </div>

      {/* Song info */}
      <div class="song-info">
        {/* Title wrapper with play button */}
        <div class="song-title-wrapper" data-spotify-id={song.spotifyId}>
          <button class="song-title-play-btn" onClick={handlePlay}>
            <div class="play-icon"></div>
            <div class="pause-icon"></div>
          </button>
          <a
            href={`https://open.spotify.com/track/${song.spotifyId}`}
            target="_blank"
            rel="noopener noreferrer"
            class="song-title spotify-content-link"
            onClick={(e) => e.stopPropagation()}
          >
            {song.title}
          </a>
          <div class="playlist-badge">
            <svg viewBox="0 0 135 135" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 67.5C5.625 33.3281 33.3281 5.625 67.5 5.625C101.672 5.625 129.375 33.3281 129.375 67.5C129.375 101.672 101.672 129.375 67.5 129.375C33.3281 129.375 5.625 101.672 5.625 67.5ZM97.8638 54.1125C98.8631 53.0468 99.4086 51.6342 99.3848 50.1735C99.3611 48.7128 98.77 47.3186 97.7367 46.2859C96.7033 45.2533 95.3087 44.6632 93.8479 44.6405C92.3872 44.6178 90.975 45.1643 89.91 46.1644L56.0981 79.9706L45.4444 69.3225C44.3835 68.2979 42.9626 67.7309 41.4877 67.7437C40.0129 67.7565 38.6021 68.3481 37.5591 69.391C36.5162 70.4339 35.9247 71.8448 35.9118 73.3196C35.899 74.7945 36.466 76.2154 37.4906 77.2763L56.0981 95.8838L97.8638 54.1181V54.1125Z" fill="#1DB954"/>
            </svg>
          </div>
        </div>

        {/* Artist */}
        <p class="song-artist">{song.artist}</p>

        {/* Album */}
        {song.album && <p class="song-album">{song.album}</p>}

        {/* Meta info */}
        <div class="song-meta-stacked">
          <div class="submitter-info">
            Submitted by{' '}
            {song.userId ? (
              <a href={`/user/${song.userId}`} class="user-name-link">
                {song.userName || 'Anonymous'}
              </a>
            ) : (
              song.userName || 'Anonymous'
            )}
          </div>
          <div class={`likes-pill ${shouldHideVoteCounts ? 'hidden-during-voting' : ''}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
            </svg>
            <span class="likes-count">{song.votes?.score || 0}</span>
          </div>
        </div>

        {/* Vote controls */}
        <div class="vote-controls">
          <button
            class={`vote-btn vote-like ${song.userVote === 'like' ? 'active' : ''} ${song.isOwnSong ? 'disabled-own-song' : ''}`}
            data-song-id={song.id}
            data-vote="like"
            disabled={song.isOwnSong}
            title={song.isOwnSong ? "You can't vote on your own submission" : undefined}
            onClick={(e) => handleVote(e, 'like')}
          >
            Like
          </button>
          <button
            class={`vote-btn vote-neutral ${song.userVote === 'neutral' ? 'active' : ''} ${song.isOwnSong ? 'disabled-own-song' : ''}`}
            data-song-id={song.id}
            data-vote="neutral"
            disabled={song.isOwnSong}
            title={song.isOwnSong ? "You can't vote on your own submission" : undefined}
            onClick={(e) => handleVote(e, 'neutral')}
          >
            Neutral
          </button>
          <button
            class={`vote-btn vote-dislike ${song.userVote === 'dislike' ? 'active' : ''} ${song.isOwnSong ? 'disabled-own-song' : ''}`}
            data-song-id={song.id}
            data-vote="dislike"
            disabled={song.isOwnSong}
            title={song.isOwnSong ? "You can't vote on your own submission" : undefined}
            onClick={(e) => handleVote(e, 'dislike')}
          >
            Dislike
          </button>
        </div>
      </div>
    </div>
  );
}
